import {useState, useEffect} from 'react';
import {useQuery} from '@apollo/client';
import {Table, Typography, Input, DatePicker, Select, Tag} from 'antd';
import {SearchOutlined} from '@ant-design/icons';
import moment from 'moment';
import {
  GET_KYC_DASHBOARD_DATA_STATS,
  KYC_DASHBOARD_DATA_TABLE,
} from 'graphql/queries/users';
import {sanitizeInput} from 'utils/helper';
import {isApprovedKycStatus} from 'utils/kycStatus';
import {
  EMPTY_CHECK_FILTERS,
  KycCheckFilters,
  KycUserCountryFilter,
  QueueFilterField,
  buildCheckFilterInput,
} from './kycQueueFilters';
import {KycUserLink} from './kycQueueShared';
import styles from './kycDashboard.module.scss';

const {Title, Text} = Typography;
const {Option} = Select;

const USER_STATUS_OPTIONS = [
  {label: 'All User Statuses', value: ''},
  {label: 'No deposits', value: 'NO_DEPOSITS'},
  {label: 'Deposited', value: 'DEPOSITED'},
  {label: 'Invested', value: 'INVESTED'},
  {label: 'Deleted', value: 'DELETED'},
];

const userStatusToUserFilter = status => {
  switch (status) {
    case 'DEPOSITED':
      return {hasMadeDeposit: 'TRUE'};
    case 'NO_DEPOSITS':
      return {hasMadeDeposit: 'FALSE'};
    case 'INVESTED':
      return {hasMadeInvestment: 'TRUE'};
    case 'DELETED':
      return {deleted: true};
    default:
      return {};
  }
};

const STAT_CARDS = [
  {key: 'totalKyc', title: 'Total KYCs'},
  {key: 'pendingKyc', title: 'Pending KYCs', className: styles.pendingCard},
  {
    key: 'successfulKyc',
    title: 'Successful KYCs',
    className: styles.successCard,
  },
  {key: 'failedKyc', title: 'Failed KYCs', className: styles.failedCard},
];

const STAT_ROWS = [
  {key: 'identityCheck', label: 'Identity check'},
  {key: 'documentCheck', label: 'Document check'},
  {key: 'aml', label: 'AML'},
];

// A dash until the numbers arrive, rather than a zero that looks like a count.
const formatCount = value =>
  typeof value === 'number' ? value.toLocaleString() : '–';

const getStatusColor = status => {
  const s = (status || '').toUpperCase();
  // Conditionally approved reads as approved here — it clears the same gates.
  // The label beside it still says which of the two it is.
  if (isApprovedKycStatus(s) || s === 'SUCCESSFUL' || s === 'INVESTED')
    return 'green';
  if (s === 'PENDING' || s === 'PENDING_VERIFICATION') return 'orange';
  if (s === 'REJECTED' || s === 'FAILED' || s === 'DELETED') return 'red';
  if (s === 'DEPOSITED') return 'blue';
  if (s === 'NO_DEPOSITS') return 'default';
  return 'default';
};

const formatStatusLabel = status => {
  if (!status) return '-';
  return status.replace(/_/g, ' ');
};

const buildUserFilters = (searchTerm, filterState) => ({
  ...(searchTerm && {username: sanitizeInput(searchTerm)}),
  ...(filterState.startDate && {
    startDate: moment(filterState.startDate).format(),
  }),
  ...(filterState.endDate && {
    endDate: moment(filterState.endDate).format(),
  }),
  ...(filterState.country && {country: filterState.country}),
  // The identity, document, AML and deposit lists. The legacy singular
  // `kycStatus` is deliberately not sent: it only restricts the identity check,
  // and supplying both applies both restrictions — so a document-only or
  // AML-only search would come back silently narrowed, or empty if the two
  // disagree.
  ...buildCheckFilterInput(filterState),
  ...userStatusToUserFilter(filterState.userStatus),
});

export const KycDashboardTab = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  // What is typed, and what has been searched for. They are two states so the
  // input stays responsive while the query waits for the typing to stop.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    ...EMPTY_CHECK_FILTERS,
    startDate: null,
    endDate: null,
    country: '',
    userStatus: '',
  });

  const {data: statsData} = useQuery(GET_KYC_DASHBOARD_DATA_STATS, {
    errorPolicy: 'ignore',
  });

  const {data, loading} = useQuery(KYC_DASHBOARD_DATA_TABLE, {
    variables: {
      filters: {
        pagination: {
          page: currentPage,
          limit,
        },
        sort: {
          order: 'DESC',
          field: 'createdAt',
        },
        user: buildUserFilters(searchTerm, filters),
      },
    },
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'ignore',
  });

  const kycStats =
    statsData?.getUserKycStatistics?.__typename === 'ResponseWithKycStatistics'
      ? statsData?.getUserKycStatistics?.data
      : null;

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    // A new page size renumbers the pages, so go back to the first one rather
    // than landing past the end of a shorter list.
    setCurrentPage(pageSize !== limit ? 1 : page);
    setLimit(pageSize);
  };

  // Wait for a pause in the typing before the search term becomes a query
  // variable. The effect owns the timer, so switching a filter mid-sentence
  // cancels the pending search instead of letting it land with the filters
  // that were selected when the key was pressed.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 600);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Takes a patch rather than one key: the four check selectors move as a
  // group, and applying them one key at a time would fire a refetch per list.
  const handleFilterChange = changes => {
    setFilters(current => ({...current, ...changes}));
    // Any filter change renumbers the results, so never keep the old page.
    setCurrentPage(1);
  };

  const columns = [
    {
      title: 'Full Name',
      key: 'fullName',
      render: (_, record) => {
        const name =
          `${record?.firstName || ''} ${record?.lastName || ''}`.trim() || '-';
        return <KycUserLink user={record}>{name}</KycUserLink>;
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
    },
    {
      title: 'Identity Check',
      key: 'identityCheck',
      render: (_, record) => {
        const status = record?.investorProfile?.identityCard?.kycStatus;
        return (
          <Tag color={getStatusColor(status)}>{formatStatusLabel(status)}</Tag>
        );
      },
    },
    {
      title: 'Document Check',
      key: 'documentCheck',
      render: (_, record) => {
        const status =
          record?.investorProfile?.identityCard?.documentCheckStatus;
        return (
          <Tag color={getStatusColor(status)}>{formatStatusLabel(status)}</Tag>
        );
      },
    },
    {
      title: 'AML',
      key: 'aml',
      render: (_, record) => {
        const status = record?.investorProfile?.identityCard?.amlStatus;
        return (
          <Tag color={getStatusColor(status)}>{formatStatusLabel(status)}</Tag>
        );
      },
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.sectionIntro}>
        <Title level={4}>Overview</Title>
        <Text type="secondary">
          Summary metrics and searchable list of users with KYC status
        </Text>
      </div>

      <div className={styles.statsCards}>
        {STAT_CARDS.map(card => (
          <div
            key={card.key}
            className={`${styles.statCard} ${card.className || ''}`.trim()}>
            <h4 className={styles.statTitle}>{card.title}</h4>
            {STAT_ROWS.map(row => (
              <div key={row.key} className={styles.statItem}>
                <span className={styles.statLabel}>{row.label}</span>
                <span className={styles.statValue}>
                  {formatCount(kycStats?.[card.key]?.[row.key])}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className={styles.filters}>
        <QueueFilterField label="Search" htmlFor="kyc-search" wide>
          <Input
            id="kyc-search"
            prefix={<SearchOutlined />}
            placeholder="Name or email"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            allowClear
          />
        </QueueFilterField>
        <QueueFilterField label="From" htmlFor="kyc-start-date">
          <DatePicker
            id="kyc-start-date"
            placeholder="Any date"
            value={filters.startDate}
            onChange={date => handleFilterChange({startDate: date})}
            allowClear
          />
        </QueueFilterField>
        <QueueFilterField label="To" htmlFor="kyc-end-date">
          <DatePicker
            id="kyc-end-date"
            placeholder="Any date"
            value={filters.endDate}
            onChange={date => handleFilterChange({endDate: date})}
            allowClear
          />
        </QueueFilterField>
        <KycUserCountryFilter value={filters} onChange={handleFilterChange} />
        <KycCheckFilters value={filters} onChange={handleFilterChange} />
        <QueueFilterField label="User status" htmlFor="kyc-user-status">
          <Select
            id="kyc-user-status"
            placeholder="All"
            value={filters.userStatus || undefined}
            onChange={value => handleFilterChange({userStatus: value || ''})}
            allowClear>
            {USER_STATUS_OPTIONS.map(option => (
              <Option key={option.value || 'all-user'} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </QueueFilterField>
      </div>

      <Table
        columns={columns}
        dataSource={data?.getAllUsers?.users || []}
        loading={loading}
        className="antd-table"
        pagination={{
          current: currentPage,
          pageSize: limit,
          showSizeChanger: true,
          showQuickJumper: true,
          total: data?.getAllUsers?.totalDocs || 0,
          onChange: handlePageChange,
        }}
        rowKey={(record, index) => record?.email || index}
      />
    </div>
  );
};
